import { add, endOfDay, format, isAfter, isBefore, isEqual, isSameDay, isWithinInterval, setHours, setMinutes, startOfDay, subSeconds } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { DATE_FORMATS_FNS } from "components";

export const toDate = (date: string | Date) => (typeof date === "string" ? new Date(date) : date);

export const formatDate = (date?: string | Date | null, formatString: DATE_FORMATS_FNS = DATE_FORMATS_FNS.ISOFormat) => {
  if (!date) return "";
  return format(toDate(date), formatString);
};

export const getFormattedDateRange = (startDate?: Date | null, endDate?: Date | null) => {
  if (!startDate || !endDate) return "";
  return `${formatDate(startDate, DATE_FORMATS_FNS.dateMonthYear)} - ${formatDate(endDate, DATE_FORMATS_FNS.dateMonthYear)}`;
};

export const formatCurrentUTCDate = (formatString: DATE_FORMATS_FNS) => {
  return formatInTimeZone(new Date(), "UTC", formatString);
};

export const isSameOrAfter = (date1: string | Date, date2: string | Date) => {
  const d1 = toDate(date1);
  const d2 = toDate(date2);

  return isAfter(d1, d2) || isEqual(d1, d2);
};

export const isSameOrBefore = (date1: string | Date, date2: string | Date) => {
  const d1 = toDate(date1);
  const d2 = toDate(date2);

  return isBefore(d1, d2) || isEqual(d1, d2);
};

export const setHoursMinutesAndFormat = (date: string | Date, hour: number, minute: number, formatString: DATE_FORMATS_FNS = DATE_FORMATS_FNS.ISOFormat) => {
  const dateObj = toDate(date);

  const updatedDateObj = setHours(dateObj, hour);
  const finalDateObj = setMinutes(updatedDateObj, minute);

  return format(finalDateObj, formatString);
};

export const addDaysToDate = (date: string | Date, days: number) => add(toDate(date), { days });

export const isDate = (date: string | Date | null) => {
  if (typeof date === "string") {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  }

  return date instanceof Date && !isNaN(date.getTime());
};

export const subtractSecondsAndFormatDate = (date: string | Date | null | undefined, duration: number, dateFormat: DATE_FORMATS_FNS) => {
  if (!date) return null;

  const dateObj = toDate(date);
  const newDate = subSeconds(dateObj, duration);
  return format(newDate, dateFormat);
};

export const isSameDayHelper = (date1: string | Date, date2: string | Date = new Date()) => isSameDay(toDate(date1), toDate(date2));

export const toISOString = (date: string | Date | null | undefined) => (date ? toDate(date) : new Date()).toISOString();

export const isDateAfter = (date1: string | Date | null | undefined, date2: string | Date | null | undefined): boolean => {
  const d1 = date1 ? toDate(date1) : new Date();
  const d2 = date2 ? toDate(date2) : new Date();
  return isAfter(d1, d2);
};

export const formatDurationFromMinutes = (minutes: number | undefined) => {
  if (!minutes) return "00:00";

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const formattedHours = String(hours).padStart(2, "0");
  const formattedMinutes = String(remainingMinutes).padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}`;
};

export const isBetween = (currentDate: string | Date, dateFrom: string | Date, dateTo: string | Date) =>
  isWithinInterval(toDate(currentDate), { start: toDate(dateFrom), end: toDate(dateTo) });

export const isDateInDayRange = (date: string | Date, startDate: string | Date, endDate: string | Date) =>
  isWithinInterval(toDate(date), { start: startOfDay(toDate(startDate)), end: endOfDay(toDate(endDate)) });
